import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
	MagnifyingGlassIcon,
	BuildingOffice2Icon,
	MapPinIcon,
	ShieldCheckIcon,
	FunnelIcon,
	CalendarDaysIcon,
	ArrowRightIcon,
	XMarkIcon,
	TagIcon,
	ClipboardDocumentCheckIcon,
	DocumentArrowDownIcon,
	DocumentIcon,
	CheckCircleIcon as CheckCircleOutline,
	ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { contentApi } from '../../api/content';

const WHATSAPP = process.env.REACT_APP_WHATSAPP || '18496079129';

const CATEGORIA_LABELS = {
	servicios: 'Servicios',
	comercio: 'Comercio',
	construccion: 'Construcción',
	inmobiliaria: 'Inmobiliaria',
	agro: 'Agrocomercio',
	otro: 'Otros',
};

const INCLUYE_DEFAULT = [
	'Cambio de socios',
	'Designación de Gerente',
	'Cambio de domicilio de la empresa',
	'Modificación de actividad comercial',
	'Cambio de informaciones de contacto',
	'Comprobantes fiscales (NCF)',
	'Sello de la empresa',
	'Entrega al día con obligaciones fiscales',
	'Acceso Oficina Virtual DGII',
	'Acceso oficina virtual TSS y MT',
];

const FAQS = [
	{
		q: '¿Qué incluye el precio?',
		a: 'Cada ficha detalla las modificaciones, documentos y accesos incluidos. Un asesor confirma el alcance final antes de formalizar la adquisición.',
	},
	{
		q: '¿Cuánto tarda el proceso?',
		a: 'El plazo depende de los cambios solicitados y las entidades involucradas. Te informamos el estimado al seleccionar una empresa.',
	},
	{
		q: '¿La empresa tiene deudas tributarias?',
		a: 'La ventana de detalle muestra el estado declarado de deuda tributaria y cualquier información relevante disponible para cada opción.',
	},
	{
		q: '¿Cómo publico mi empresa para venderla?',
		a: 'Completa la solicitud en nuestra página "Vender mi empresa". Revisaremos la documentación y, si cumple los criterios, prepararemos la ficha para compradores.',
	},
	{
		q: '¿Puedo verificar la documentación antes de comprar?',
		a: 'Sí. Antes de concretar la adquisición te presentamos la documentación disponible y el estado reportado de la empresa seleccionada.',
	},
];

function formatFechaLarga(iso) {
	try {
		const d = new Date(`${iso}T12:00:00`);
		return d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short', year: 'numeric' });
	} catch {
		return iso;
	}
}

function precioFmt(n) {
	return new Intl.NumberFormat('es-DO', {
		style: 'currency',
		currency: 'DOP',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(n);
}

function waUrl(text) {
	return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(text)}`;
}

function useReveal(threshold = 0.1) {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const obs = new IntersectionObserver(
			([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
			{ threshold }
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, [threshold]);
	return [ref, visible];
}

function RevealSection({ children, className = '' }) {
	const [ref, visible] = useReveal();
	return (
		<div
			ref={ref}
			className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
		>
			{children}
		</div>
	);
}

function BoolBadge({ value }) {
	return (
		<span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${value ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
			{value ? 'Sí' : 'No'}
		</span>
	);
}

function FaqItem({ q, a, open, onToggle }) {
	return (
		<article className="border border-slate-200 rounded-xl bg-white overflow-hidden">
			<button
				type="button"
				onClick={onToggle}
				aria-expanded={open}
				className="flex items-center justify-between w-full px-5 py-4 text-left font-semibold text-slate-800 hover:text-primary transition-colors"
			>
				<span>{q}</span>
				<ChevronDownIcon className={`h-5 w-5 text-secondary flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
			</button>
			<div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
				<div className="overflow-hidden">
					<p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</p>
				</div>
			</div>
		</article>
	);
}

export default function EmpresasEnCarpeta() {
	const [loading, setLoading] = useState(true);
	const [empresas, setEmpresas] = useState([]);
	const [q, setQ] = useState('');
	const [anio, setAnio] = useState('todas');
	const [categoria, setCategoria] = useState('todas');
	const [orden, setOrden] = useState('recientes');
	const [selectedEmpresa, setSelectedEmpresa] = useState(null);
	const [empresaDocs, setEmpresaDocs] = useState([]);
	const [docsLoading, setDocsLoading] = useState(false);
	const [openFaq, setOpenFaq] = useState(0);
	const [actividadOpen, setActividadOpen] = useState(false);

	const loadDocsForEmpresa = useCallback(async (empresaId) => {
		if (!empresaId) { setEmpresaDocs([]); return; }
		try {
			setDocsLoading(true);
			const res = await contentApi.getDocumentosEmpresa(empresaId);
			setEmpresaDocs(Array.isArray(res) ? res : []);
		} catch {
			setEmpresaDocs([]);
		} finally {
			setDocsLoading(false);
		}
	}, []);

	const handleSelectEmpresa = useCallback((emp) => {
		setSelectedEmpresa(emp);
		setActividadOpen(false);
		loadDocsForEmpresa(emp?.id);
	}, [loadDocsForEmpresa]);

	useEffect(() => {
		(async () => {
			try {
				setLoading(true);
				const res = await contentApi.getEmpresasCarpetaData();
				setEmpresas(Array.isArray(res?.items) ? res.items : []);
			} catch {
				setEmpresas([]);
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	const anios = useMemo(() =>
		[...new Set(empresas.map((e) => String(e.fecha_constitucion || '').slice(0, 4)).filter(Boolean))].sort((a, b) => Number(b) - Number(a)),
		[empresas]
	);

	const categorias = useMemo(() => {
		const cats = [...new Set(empresas.filter(e => e.activa && e.categoria).map(e => e.categoria))];
		return cats.sort();
	}, [empresas]);

	const filtradas = useMemo(() => {
		let list = [...empresas].filter((e) => e && e.activa);
		const term = q.trim().toLowerCase();
		if (term) {
			list = list.filter(
				(e) =>
					(e.nombre_empresa || '').toLowerCase().includes(term) ||
					(e.actividad || '').toLowerCase().includes(term) ||
					(e.ubicacion || '').toLowerCase().includes(term) ||
					(e.resumen || '').toLowerCase().includes(term)
			);
		}
		if (anio !== 'todas') {
			list = list.filter((e) => String(e.fecha_constitucion || '').slice(0, 4) === anio);
		}
		if (categoria !== 'todas') {
			list = list.filter((e) => (e.categoria || 'servicios') === categoria);
		}
		if (orden === 'precio_asc') list.sort((a, b) => Number(a.costo || 0) - Number(b.costo || 0));
		else if (orden === 'precio_desc') list.sort((a, b) => Number(b.costo || 0) - Number(a.costo || 0));
		else if (orden === 'nombre') list.sort((a, b) => (a.nombre_empresa || '').localeCompare(b.nombre_empresa || ''));
		else list.sort((a, b) => new Date(b.fecha_constitucion || 0) - new Date(a.fecha_constitucion || 0));
		return list;
	}, [empresas, q, anio, categoria, orden]);

	const totalActivas = useMemo(() => empresas.filter(e => e.activa).length, [empresas]);

	const abrirWhatsapp = (emp) => {
		const lines = [
			'Hola, me interesa esta empresa en carpeta:',
			`Nombre: ${emp.nombre_empresa || 'N/D'}`,
			`Costo: ${precioFmt(emp.costo || 0)}`,
			`Fecha constitución: ${emp.fecha_constitucion || 'N/D'}`,
			`Ubicación: ${emp.ubicacion || 'N/D'}`,
			`Actividad: ${emp.actividad || 'N/D'}`,
		];
		window.open(waUrl(lines.join('\n')), '_blank', 'noopener,noreferrer');
	};

	const limpiarFiltros = () => { setQ(''); setAnio('todas'); setCategoria('todas'); };
	const hayFiltros = q || anio !== 'todas' || categoria !== 'todas';

	return (
		<div className="min-h-screen bg-slate-50">

			{/* ── Hero ── */}
			<section
				className="relative pt-14 pb-32 text-white overflow-hidden"
				style={{
					backgroundColor: 'var(--color-primary)',
					backgroundImage: `
						linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px),
						linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px)
					`,
					backgroundSize: '48px 48px',
				}}
			>
				<div className="absolute inset-0 bg-gradient-to-b from-primary/0 via-primary/0 to-primary/80 pointer-events-none" />
				<div className="container-width relative px-4 text-center">
					<p className="text-xs font-bold uppercase tracking-widest text-white/60 mb-4">
						Empresas en carpeta · República Dominicana
					</p>
					<h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
						Compra una empresa ya constituida
						<br className="hidden sm:block" /> y lista para transferir
					</h1>
					<p className="text-white/80 text-lg sm:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
						Empresas verificadas con acompañamiento legal y fiscal durante todo el proceso de adquisición.
					</p>
					<div className="flex flex-col sm:flex-row items-center justify-center gap-3">
						<a
							href="#empresas"
							className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-lg shadow-emerald-600/30 transition-all"
						>
							<BuildingOffice2Icon className="h-5 w-5" />
							Ver empresas disponibles
						</a>
						<Link
							to="/vender-empresa"
							className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/40 hover:bg-white/10 text-white font-semibold transition-all"
						>
							¿Quieres vender tu empresa? →
						</Link>
					</div>
				</div>
			</section>

			{/* ── Trust strip ── */}
			<div className="container-width px-4 -mt-12 relative z-10 mb-12">
				<div className="bg-white rounded-2xl shadow-xl border border-slate-100 grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100">
					{[
						{ icon: <BuildingOffice2Icon className="h-6 w-6 text-primary" />, value: `${totalActivas}`, label: 'empresas disponibles' },
						{ icon: <CheckCircleOutline className="h-6 w-6 text-emerald-500" />, value: 'Documentación', label: 'verificada' },
						{ icon: <ShieldCheckIcon className="h-6 w-6 text-secondary" />, value: 'Acompañamiento', label: 'legal y fiscal' },
						{ icon: <CalendarDaysIcon className="h-6 w-6 text-primary" />, value: 'Actualizado', label: 'mayo 2026' },
					].map(({ icon, value, label }, i) => (
						<div key={i} className="flex items-center gap-3 px-4 py-4 justify-center md:justify-start">
							<div className="flex-shrink-0 p-2 rounded-xl bg-slate-50">{icon}</div>
							<div>
								<p className="font-bold text-slate-800 text-sm leading-tight">{value}</p>
								<p className="text-xs text-slate-500">{label}</p>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* ── ¿Qué es una empresa en carpeta? ── */}
			<RevealSection>
				<section className="container-width px-4 pb-16">
					<div className="text-center max-w-2xl mx-auto mb-10">
						<p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Una solución rápida y segura</p>
						<h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">¿Qué es una empresa en carpeta?</h2>
						<p className="text-slate-500 leading-relaxed">
							Es una sociedad ya constituida que puede ser adquirida y adaptada a tu operación, reduciendo tiempos de inicio y evitando trámites desde cero.
						</p>
					</div>
					<div className="grid sm:grid-cols-3 gap-5">
						{[
							{ n: '01', title: 'Lista para transferir', desc: 'Selecciona una entidad constituida con precio y antigüedad visibles.' },
							{ n: '02', title: 'Información revisada', desc: 'Consulta su situación documental y fiscal antes de decidir.' },
							{ n: '03', title: 'Proceso acompañado', desc: 'Gestionamos las modificaciones necesarias y la entrega final.' },
						].map(({ n, title, desc }) => (
							<article key={n} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
								<div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary font-bold text-sm mb-4">{n}</div>
								<h3 className="font-bold text-slate-900 mb-2">{title}</h3>
								<p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
							</article>
						))}
					</div>
				</section>
			</RevealSection>

			{/* ── Catálogo ── */}
			<section id="empresas" className="bg-white py-12">
				<div className="container-width px-4">
					<div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
						<div>
							<p className="text-xs font-bold uppercase tracking-widest text-secondary mb-1">Catálogo disponible</p>
							<h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Elige tu empresa</h2>
						</div>
						<p className="text-sm text-slate-500 max-w-xs">Compara precio, ubicación, antigüedad y beneficios incluidos.</p>
					</div>

					{/* Filtros */}
					<div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 md:p-6 mb-8 space-y-4">
						<div className="flex items-center gap-2 mb-1">
							<FunnelIcon className="h-4 w-4 text-primary" />
							<span className="text-sm font-semibold text-slate-700">Filtros</span>
							{hayFiltros && (
								<button type="button" onClick={limpiarFiltros} className="ml-auto text-xs text-secondary font-semibold hover:underline">
									Limpiar filtros
								</button>
							)}
						</div>

						{/* Search + sort */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
							<div className="relative">
								<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
								<input
									type="search"
									placeholder="Buscar por nombre, actividad o ubicación"
									value={q}
									onChange={(e) => setQ(e.target.value)}
									className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-secondary focus:border-transparent outline-none text-sm"
								/>
							</div>
							<select
								value={orden}
								onChange={(e) => setOrden(e.target.value)}
								className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-800 focus:ring-2 focus:ring-secondary outline-none text-sm font-medium"
							>
								<option value="recientes">Más recientes</option>
								<option value="nombre">Nombre: A → Z</option>
								<option value="precio_asc">Precio: menor a mayor</option>
								<option value="precio_desc">Precio: mayor a menor</option>
							</select>
						</div>

						{/* Categoría pills */}
						{categorias.length > 0 && (
							<div>
								<p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Actividad</p>
								<div className="flex flex-wrap gap-2">
									<button
										type="button"
										onClick={() => setCategoria('todas')}
										className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${categoria === 'todas' ? 'bg-primary text-white shadow' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary/40'}`}
									>
										Todas
									</button>
									{categorias.map((cat) => (
										<button
											key={cat}
											type="button"
											onClick={() => setCategoria(cat)}
											className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${categoria === cat ? 'bg-primary text-white shadow' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary/40'}`}
										>
											{CATEGORIA_LABELS[cat] || cat}
										</button>
									))}
								</div>
							</div>
						)}

						{/* Año pills */}
						{anios.length > 0 && (
							<div>
								<p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Año de constitución</p>
								<div className="flex flex-wrap gap-2">
									<button
										type="button"
										onClick={() => setAnio('todas')}
										className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${anio === 'todas' ? 'bg-secondary text-white shadow' : 'bg-white border border-slate-200 text-slate-600 hover:border-secondary/40'}`}
									>
										Todos
									</button>
									{anios.map((y) => (
										<button
											key={y}
											type="button"
											onClick={() => setAnio(String(y))}
											className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${anio === String(y) ? 'bg-secondary text-white shadow' : 'bg-white border border-slate-200 text-slate-600 hover:border-secondary/40'}`}
										>
											{y}
										</button>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Resultados */}
					<div className="flex items-center justify-between mb-4 text-sm text-slate-500">
						<p>
							<strong className="text-slate-800">{filtradas.length}</strong>{' '}
							{filtradas.length === 1 ? 'empresa encontrada' : 'empresas encontradas'}
						</p>
					</div>

					{loading ? (
						<div className="text-center py-16 text-slate-400">Cargando empresas...</div>
					) : filtradas.length === 0 ? (
						<div className="text-center py-16">
							<p className="text-slate-500 mb-3">No hay empresas que coincidan con tu búsqueda.</p>
							<button type="button" onClick={limpiarFiltros} className="text-secondary font-semibold hover:underline text-sm">
								Limpiar filtros
							</button>
						</div>
					) : (
						<div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
							{filtradas.map((emp) => {
								const catLabel = CATEGORIA_LABELS[emp.categoria] || emp.categoria || 'Servicios';
								const cardSummary = emp.resumen || emp.actividad || 'Sin descripción registrada';
								return (
									<article
										key={emp.id}
										className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm ring-1 ring-slate-900/5 overflow-hidden hover:shadow-xl hover:-translate-y-1.5 hover:ring-primary/20 transition-all duration-300"
									>
										<div className="h-1.5 w-full bg-gradient-to-r from-primary via-secondary to-primary/60 shrink-0" />
										<div className="p-5 flex-1 flex flex-col">
											<span className="inline-flex self-start items-center gap-1 rounded-full bg-secondary/10 text-secondary text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 mb-3">
												<TagIcon className="h-3 w-3" />
												{catLabel}
											</span>
											<div className="flex items-start gap-3 mb-3">
												<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
													<BuildingOffice2Icon className="h-5 w-5" />
												</div>
												<h2 className="font-bold text-slate-900 uppercase tracking-wide text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">
													{emp.nombre_empresa}
												</h2>
											</div>
											<p className="text-slate-500 text-sm leading-relaxed flex-1 mb-4 line-clamp-3">
												{cardSummary}
											</p>
											<div className="space-y-1.5 text-xs text-slate-400 mb-4">
												<div className="flex items-center gap-1.5">
													<MapPinIcon className="h-3.5 w-3.5 flex-shrink-0 text-secondary" />
													<span className="truncate">{emp.ubicacion || 'N/D'}</span>
												</div>
												<div className="flex items-center gap-1.5">
													<CalendarDaysIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
													<span>Constituida: {emp.fecha_constitucion ? formatFechaLarga(emp.fecha_constitucion) : 'N/D'}</span>
												</div>
											</div>
											<div className="flex items-end justify-between gap-2 pt-3 border-t border-slate-100 mt-auto">
												<div>
													<p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Inversión</p>
													<p className="text-lg font-extrabold text-primary tabular-nums">{precioFmt(emp.costo || 0)}</p>
												</div>
												<button
													type="button"
													onClick={() => handleSelectEmpresa(emp)}
													className="inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:gap-2 transition-all"
												>
													Ver detalles
													<ArrowRightIcon className="h-4 w-4" />
												</button>
											</div>
										</div>
									</article>
								);
							})}
						</div>
					)}
				</div>
			</section>

			{/* ── Proceso de compra ── */}
			<RevealSection>
				<section className="container-width px-4 py-16">
					<div className="text-center max-w-xl mx-auto mb-10">
						<p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Proceso de compra</p>
						<h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Adquiere tu empresa en 3 pasos</h2>
					</div>
					<div className="grid sm:grid-cols-3 gap-5">
						{[
							{ n: '1', title: 'Elige una empresa', desc: 'Revisa las opciones disponibles y consulta la que se ajuste a tu proyecto o necesidad.' },
							{ n: '2', title: 'Validamos documentos', desc: 'Confirmamos la información y te explicamos alcance, costo total y requisitos.' },
							{ n: '3', title: 'Transferimos y entregamos', desc: 'Gestionamos el cambio correspondiente y entregamos accesos y documentación.' },
						].map(({ n, title, desc }, i) => (
							<article key={n} className="relative bg-slate-50 rounded-2xl p-6">
								{i < 2 && (
									<span className="hidden sm:block absolute top-10 -right-3 text-slate-300 text-2xl font-bold z-10">→</span>
								)}
								<div className="flex items-center justify-center w-11 h-11 rounded-full bg-primary text-white font-bold text-lg mb-4">{n}</div>
								<h3 className="font-bold text-slate-900 mb-2">{title}</h3>
								<p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
							</article>
						))}
					</div>
				</section>
			</RevealSection>

			{/* ── Verificación / Confianza ── */}
			<RevealSection>
				<section className="bg-white py-14">
					<div className="container-width px-4">
						<div className="grid md:grid-cols-2 gap-10 items-center">
							<div>
								<p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Confianza antes de comprar</p>
								<h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Compra con tranquilidad</h2>
								<p className="text-slate-500 leading-relaxed mb-6">
									Antes de concretar la adquisición, te presentamos la documentación aplicable y el estado reportado de la empresa seleccionada.
								</p>
								<ul className="space-y-3">
									{[
										'Documentación societaria disponible para revisión',
										'Estado fiscal indicado para cada opción',
										'Cambios corporativos incluidos claramente detallados',
										'Acompañamiento durante todo el proceso de traspaso',
									].map((item) => (
										<li key={item} className="flex items-start gap-3 text-sm text-slate-600">
											<CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
											{item}
										</li>
									))}
								</ul>
							</div>
							<div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 shadow-sm">
								<p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Elementos de revisión</p>
								<div className="grid grid-cols-3 gap-3 mb-6">
									{[
										{ label: 'DGII', status: 'Revisado' },
										{ label: 'Reg. Mercantil', status: 'Revisado' },
										{ label: 'TSS', status: 'Según aplique' },
									].map(({ label, status }) => (
										<div key={label} className="bg-white rounded-xl p-3 text-center border border-slate-200">
											<p className="text-xs font-bold text-slate-700">{label}</p>
											<p className="text-[10px] font-semibold text-secondary mt-1">{status}</p>
										</div>
									))}
								</div>
								<blockquote className="text-sm text-slate-600 italic leading-relaxed mb-3 border-l-2 border-secondary pl-4">
									"Recibimos orientación clara sobre documentos, cambios y costo total antes de realizar el proceso."
								</blockquote>
								<p className="text-xs font-bold text-slate-700">Cliente empresarial <span className="text-slate-400 font-normal">· Santo Domingo</span></p>
							</div>
						</div>
					</div>
				</section>
			</RevealSection>

			{/* ── FAQ ── */}
			<RevealSection>
				<section className="container-width px-4 py-16">
					<div className="grid md:grid-cols-[0.8fr_1fr] gap-10">
						<div>
							<p className="text-xs font-bold uppercase tracking-widest text-secondary mb-2">Preguntas frecuentes</p>
							<h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Resuelve tus dudas antes de decidir</h2>
							<a
								href={waUrl('una duda sobre el proceso de adquirir una empresa en carpeta')}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline"
							>
								Hacer otra pregunta por WhatsApp →
							</a>
						</div>
						<div className="space-y-3">
							{FAQS.map((faq, i) => (
								<FaqItem
									key={i}
									q={faq.q}
									a={faq.a}
									open={openFaq === i}
									onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
								/>
							))}
						</div>
					</div>
				</section>
			</RevealSection>

			{/* ── Closing CTA ── */}
			<section
				className="py-14 text-white"
				style={{ backgroundColor: 'var(--color-primary)' }}
			>
				<div className="container-width px-4 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
					<div>
						<p className="text-xs font-bold uppercase tracking-widest text-white/60 mb-2">Asesoría personalizada</p>
						<h2 className="text-2xl sm:text-3xl font-bold mb-2">Compra o vende una empresa con acompañamiento</h2>
						<p className="text-white/75 text-sm max-w-lg leading-relaxed">
							Conversa con un asesor y recibe orientación confidencial para tu objetivo empresarial.
						</p>
					</div>
					<div className="flex flex-col gap-3 md:min-w-[260px]">
						<a
							href={waUrl('comprar o vender una empresa en carpeta')}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-lg transition-all"
						>
							Hablar por WhatsApp
						</a>
						<Link
							to="/vender-empresa"
							className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/30 hover:bg-white/10 text-white font-semibold text-sm transition-all"
						>
							Solicitar publicar mi empresa →
						</Link>
						<p className="text-center text-white/50 text-xs">Respuesta en horario laboral · +1 (849) 607-9129</p>
					</div>
				</div>
			</section>

			{/* ── Modal detalle empresa ── */}
			{selectedEmpresa && (
				<div
					className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
					onClick={() => { setSelectedEmpresa(null); setEmpresaDocs([]); }}
				>
					<div
						className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header */}
						<div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-4">
							<div className="flex items-center gap-3 min-w-0">
								<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow">
									<BuildingOffice2Icon className="h-6 w-6" />
								</div>
								<div className="min-w-0">
									<p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Empresa en carpeta</p>
									<h3 className="font-extrabold text-primary uppercase tracking-tight text-lg leading-tight truncate">
										{selectedEmpresa.nombre_empresa}
									</h3>
								</div>
							</div>
							<div className="flex items-center gap-2 flex-shrink-0">
								<span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
									✓ Verificada
								</span>
								<button
									type="button"
									onClick={() => { setSelectedEmpresa(null); setEmpresaDocs([]); }}
									className="p-2 rounded-full text-slate-400 hover:text-primary hover:bg-slate-100 transition-colors"
								>
									<XMarkIcon className="h-5 w-5" />
								</button>
							</div>
						</div>

						<div className="p-6 space-y-5">
							{/* Métricas */}
							<div className="grid grid-cols-3 border border-slate-200 rounded-xl overflow-hidden divide-x divide-slate-200">
								<div className="px-4 py-3">
									<p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Constituida</p>
									<p className="font-semibold text-slate-800 text-sm">{selectedEmpresa.fecha_constitucion || 'N/D'}</p>
								</div>
								<div className="px-4 py-3">
									<p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Ubicación</p>
									<p className="font-semibold text-slate-800 text-sm leading-tight line-clamp-2">{selectedEmpresa.ubicacion || 'N/D'}</p>
								</div>
								<div className="px-4 py-3">
									<p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Precio</p>
									<p className="font-extrabold text-primary text-base">{precioFmt(selectedEmpresa.costo || 0)}</p>
								</div>
							</div>

							{/* Resumen / Actividad */}
							{selectedEmpresa.resumen && (
								<div>
									<p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Resumen</p>
									<p className="text-sm text-slate-700 leading-relaxed">{selectedEmpresa.resumen}</p>
								</div>
							)}

							{/* Descripción adicional */}
							{selectedEmpresa.descripcion && (
								<div>
									<p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">Descripción</p>
									<p className="text-sm text-slate-600 leading-relaxed">{selectedEmpresa.descripcion}</p>
								</div>
							)}

							{/* Grid: Incluye + Estado */}
							<div className="grid md:grid-cols-[1.1fr_0.9fr] gap-4">
								{/* Incluye */}
								<div className="rounded-xl border border-slate-200 overflow-hidden">
									<div className="flex items-center gap-2 px-4 py-3 bg-primary/5 border-b border-slate-200">
										<ClipboardDocumentCheckIcon className="h-5 w-5 text-primary" />
										<p className="font-bold text-primary text-sm uppercase tracking-wide">Incluye</p>
									</div>
									<ul className="p-3 space-y-1.5">
										{(selectedEmpresa.incluye?.length ? selectedEmpresa.incluye : INCLUYE_DEFAULT).map((item) => (
											<li key={item} className="flex items-start gap-2 text-sm text-slate-600">
												<CheckCircleIcon className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
												{item}
											</li>
										))}
									</ul>
								</div>

								{/* Estado indicado */}
								<div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
									<p className="font-bold text-slate-700 text-sm mb-3">Estado indicado</p>
									<div className="space-y-2.5">
										{[
											{ label: 'Cuenta Bancaria', val: selectedEmpresa.cuenta_bancaria },
											{ label: 'RPE', val: selectedEmpresa.rpe },
											{ label: 'Deuda Tributaria', val: selectedEmpresa.deudas_tributarias },
											{ label: 'Saldo ITBIS', val: selectedEmpresa.saldo_favor_itbis },
											{ label: 'Saldo IR-2', val: selectedEmpresa.saldo_favor_ir2 },
										].map(({ label, val }) => (
											<div key={label} className="flex items-center justify-between text-xs">
												<span className="text-slate-500">{label}</span>
												<BoolBadge value={val} />
											</div>
										))}
									</div>
								</div>
							</div>

							{/* Actividad completa colapsable */}
							{selectedEmpresa.actividad && (
								<div className="border-t border-slate-100 pt-3">
									<button
										type="button"
										onClick={() => setActividadOpen((v) => !v)}
										className="flex items-center gap-1 text-sm font-semibold text-secondary hover:underline"
									>
										<ChevronDownIcon className={`h-4 w-4 transition-transform ${actividadOpen ? 'rotate-180' : ''}`} />
										{actividadOpen ? 'Ocultar actividad registrada' : 'Ver actividad registrada completa'}
									</button>
									{actividadOpen && (
										<p className="mt-2 text-sm text-slate-500 leading-relaxed">{selectedEmpresa.actividad}</p>
									)}
								</div>
							)}

							{/* Documentos descargables */}
							{docsLoading ? (
								<div className="text-center py-3 text-sm text-slate-400">Cargando documentos...</div>
							) : empresaDocs.length > 0 && (
								<div className="rounded-xl border border-slate-200 overflow-hidden">
									<div className="flex items-center gap-2 px-4 py-3 bg-secondary/5 border-b border-slate-200">
										<DocumentArrowDownIcon className="h-5 w-5 text-secondary" />
										<p className="font-bold text-primary text-sm uppercase tracking-wide">Documentos disponibles</p>
									</div>
									<div className="p-3 space-y-2">
										{empresaDocs.map((doc) => (
											<a
												key={doc.id}
												href={doc.archivo_url}
												target="_blank"
												rel="noopener noreferrer"
												className="flex items-center gap-3 rounded-lg bg-white border border-slate-100 px-3 py-2.5 hover:border-primary/30 hover:shadow-sm transition-all group"
											>
												<DocumentIcon className="h-4 w-4 text-primary/70 flex-shrink-0" />
												<div className="min-w-0 flex-1">
													<p className="text-sm font-semibold text-slate-800 group-hover:text-primary truncate">{doc.titulo}</p>
													<p className="text-xs text-slate-400">{doc.tipo_display}{doc.descripcion ? ` · ${doc.descripcion}` : ''}</p>
												</div>
												<span className="text-xs font-bold text-secondary flex-shrink-0">Descargar</span>
											</a>
										))}
									</div>
								</div>
							)}
						</div>

						{/* Footer del modal */}
						<div className="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row gap-3 justify-end">
							<button
								type="button"
								onClick={() => { setSelectedEmpresa(null); setEmpresaDocs([]); }}
								className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors"
							>
								Seguir viendo
							</button>
							<button
								type="button"
								onClick={() => abrirWhatsapp(selectedEmpresa)}
								className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors"
							>
								Consultar por WhatsApp
							</button>
						</div>
					</div>
				</div>
			)}

			{/* ── Botón flotante mobile ── */}
			<a
				href={waUrl('comprar o vender una empresa en carpeta')}
				target="_blank"
				rel="noopener noreferrer"
				className="md:hidden fixed bottom-4 left-4 right-4 z-40 flex items-center justify-center h-13 py-3 px-4 rounded-xl bg-emerald-600 text-white font-bold shadow-xl shadow-emerald-700/30 text-sm"
			>
				Consultar por WhatsApp
			</a>
		</div>
	);
}
